import { ValidationError } from '@multiplier/lib-shared-errors';
import { z } from 'zod';
import { AuditActorTypeValues } from '../AuditActorType.js';
import { OrganizationIdSchema } from './OrganizationSchemas.js';
import { UserIdSchema } from './UserSchemas.js';
// ============================================================================
// Audit Schemas
// ============================================================================
/**
 * Actor types for audit logging
 */
export const AuditActorTypeSchema = z.enum(AuditActorTypeValues);
/**
 * Base fields shared by both user and system audit contexts
 */
const BaseAuditContextSchema = z.object({
    organizationId: OrganizationIdSchema.optional(),
    ipAddressHash: z.string().optional(),
    sessionId: z.string().optional(),
    correlationId: z.string(),
    requestId: z.string(),
    isAdmin: z.boolean().optional(),
    // When true, audit logging is skipped for the operation. Used by the
    // bulk-import flow so imported jobs/tasks/blocks/comments don't pollute
    // the activity log with the importer's name on every entry.
    bulkImport: z.boolean().optional(),
});
/**
 * Audit context for user-initiated operations.
 * userId is required when actorType is 'user'.
 */
export const UserAuditContextSchema = BaseAuditContextSchema.extend({
    actorType: z.literal('user'),
    userId: UserIdSchema,
});
/**
 * Audit context for system-initiated operations.
 * userId is not allowed when actorType is 'system' (will be stored as null).
 */
export const SystemActorAuditContextSchema = BaseAuditContextSchema.extend({
    actorType: z.literal('system'),
});
/**
 * Audit context for contact-initiated operations (external users).
 * contactId is required when actorType is 'contact'.
 * NOTE: Context uses branded ContactId at compile-time for type safety,
 * but audit log stores it as plain UUID string in userId column.
 */
export const ContactAuditContextSchema = BaseAuditContextSchema.extend({
    actorType: z.literal('contact'),
    contactId: z.string().uuid(), // Will be branded as ContactId by consumers
});
/**
 * Audit context for tracking who performed an operation and when.
 * Uses discriminated union on actorType:
 * - 'user': requires userId
 * - 'system': userId not allowed (stored as null in DB)
 * - 'contact': requires contactId (external users)
 */
export const AuditContextSchema = z.discriminatedUnion('actorType', [
    UserAuditContextSchema,
    SystemActorAuditContextSchema,
    ContactAuditContextSchema,
]);
/**
 * Type guard to check if an AuditContext is a UserAuditContext.
 * Use this to narrow the type and safely access userId.
 */
export function isUserAuditContext(context) {
    return context.actorType === 'user';
}
/**
 * Type guard to check if an AuditContext is a SystemActorAuditContext.
 */
export function isSystemAuditContext(context) {
    return context.actorType === 'system';
}
/**
 * Type guard to check if an AuditContext is a ContactAuditContext.
 * Use this to narrow the type and safely access contactId.
 */
export function isContactAuditContext(context) {
    return context.actorType === 'contact';
}
/**
 * Get the userId from an AuditContext, throwing if it's a system context.
 * Use this when the operation requires a user (e.g., permission checks).
 */
export function requireUserId(context) {
    if (context.actorType !== 'user') {
        throw new ValidationError('Operation requires a user context but received a non-user context');
    }
    return context.userId;
}
/**
 * Get the contactId from an AuditContext, throwing if it's not a contact context.
 * Use this when the operation requires a contact (e.g., external user operations).
 */
export function requireContactId(context) {
    if (context.actorType !== 'contact') {
        throw new ValidationError('Operation requires a contact context but received a different actor type');
    }
    return context.contactId;
}
/**
 * Enum of services that can call system APIs.
 * New clients must extend this enum to use system endpoints.
 */
export const SystemClientServiceSchema = z.enum(['platform-service', 'platform-workflows']);
/**
 * Enum of request categories that determine sync behavior.
 * The category indicates WHERE the update originated, which determines
 * what downstream sync tasks should be created:
 *
 * - stripe-webhook: Data FROM Stripe - don't sync back to Stripe (avoid circular)
 * - api-update: User/workflow initiated - sync to Stripe
 * - xero-webhook: Data FROM Xero - sync to Stripe
 */
export const SystemRequestCategorySchema = z.enum(['stripe-webhook', 'api-update', 'xero-webhook']);
/**
 * Audit context for system API calls.
 * Identifies the calling service and request category for traceability
 * and sync behavior determination.
 */
export const SystemAuditContextSchema = z.object({
    clientService: SystemClientServiceSchema,
    requestCategory: SystemRequestCategorySchema,
});
// ============================================================================
// Pagination Schemas
// ============================================================================
/**
 * Pagination metadata for list responses
 */
export const PaginationMetadataSchema = z.object({
    page: z.number().int().min(1),
    limit: z.number().int().min(1).max(500),
    totalItems: z.number().int().min(0),
    totalPages: z.number().int().min(0),
    hasNextPage: z.boolean(),
    hasPreviousPage: z.boolean(),
});
/**
 * Query parameters for paginated list endpoints
 */
export const PaginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
/**
 * Cursor-based pagination metadata for list responses
 * Provides stable pagination for dynamic collections where items may be added/removed
 */
export const CursorPaginationMetadataSchema = z.object({
    cursor: z.string().nullable(),
    nextCursor: z.string().nullable(),
    previousCursor: z.string().nullable(),
    limit: z.number().int().min(1).max(1000),
    hasNextPage: z.boolean(),
    hasPreviousPage: z.boolean(),
    totalItems: z.number().int().min(0).optional(),
});
/**
 * Query parameters for cursor-based paginated list endpoints
 */
export const CursorPaginationQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(1000).default(20),
});
/**
 * Query schema for including soft-deleted entities in GET endpoints
 * Uses snake_case to match existing query parameter conventions (sort_by, sort_order)
 */
export const SoftDeleteQuerySchema = z.object({
    include_deleted: z
        .enum(['true', 'false'])
        .optional()
        .default('false')
        .transform((val) => val === 'true'),
});
// ============================================================================
// Common Response Schemas
// ============================================================================
/**
 * Standard error response schema
 */
export const ErrorResponseSchema = z.object({
    error: z.string().describe('Error type or code'),
    message: z.string().describe('Human-readable error message'),
    timestamp: z.iso.datetime().describe('Error timestamp in ISO format'),
    statusCode: z.number().describe('HTTP status code'),
    details: z.looseObject({}).optional().describe('Additional error details and context'),
});
//# sourceMappingURL=ApiSchemas.js.map